<template>
  <div
    no-body
    :class="cardClass"
    class="cia-card d-flex flex-column"
    :style="cardStyle"
    @click="handleCardClick"
    @click.middle="handleCardClick"
    @keyup.enter="handleCardEnter"
    :tabindex="cardTabIndex">
    <div class="overflow-hidden">
      <img
        ref="image"
        :data-src="imageSource"
        :alt="imageAlt"
        class="cia-image cia-lazy card-img" />
    </div>

    <div class="p-0 d-flex flex-column flex-grow-1">
      <div
        :class="cardBodyClass"
        class="cia-details px-space-xs pt-space-xs mx-space-xs d-flex flex-column h-100">
        <div class="mb-auto">
          <h3 class="font-weight-bold mb-space-sm" :class="titleClass">
            {{ title }}
          </h3>

          <div v-if="displayLabelText">
            <p class="fs-xs mb-space-sm">
              {{ date }} <span class="font-weight-bold">&#xb7;</span>
              {{ minutes }} min. read
            </p>
          </div>
        </div>

        <slot />

        <div class="flex-grow-1 d-flex align-items-end">
          <a
            :target="ctaLinkTarget"
            v-if="displayCta"
            :aria-label="title + '-' + ctaText"
            :href="ctaLink"
            :class="ctaClass"
            @click="handleCtaClick"
            class="rds-link"
            >{{ ctaText }}</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted, ref, watch } from "vue";

interface Props {
  displayBorder?: boolean;
  imageSource: string;
  imageAlt: string;
  clickableCard?: boolean;
  displayLabelText?: boolean;
  date?: string;
  minutes?: string;
  bgVariant?: string;
  title: string;
  titleSize?: "small" | "medium" | "large" | "xl";
  titleVariant?: string;
  borderVariant?: string;
  displayCta?: boolean;
  ctaText?: string;
  ctaLink?: string;
  ctaTextSize?: "small" | "medium" | "large" | "xl";
  ctaTextVariant?: string;
  ctaLinkTarget?: "_blank" | "_parent" | "_self" | "_top";
  ctaTextWeight?: string;
}

const props = withDefaults(defineProps<Props>(), {
  displayBorder: true,
  clickableCard: false,
  displayLabelText: true,
  date: "February 16, 2021",
  minutes: "2",
  bgVariant: "white",
  titleSize: "medium",
  titleVariant: "dark-3",
  borderVariant: "light-4",
  displayCta: true,
  ctaText: "Read More",
  ctaLink: "https://asuonline.asu.edu/",
  ctaTextSize: "medium",
  ctaTextVariant: "primary",
  ctaLinkTarget: "_self",
  ctaTextWeight: "normal",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when any part of the card is clicked except for cta click.
   */
  (e: "cardClick", event: Event): void;
  /**
   *  Emitted when the cta is clicked, if present.
   */
  (e: "ctaClick", event: Event): void;
  /**
   *  Emitted when any the enter key is pressed while the card has focus and clickable-card is set to true.
   */
  (e: "cardEnter", event: Event): void;
}>();

const image = ref<HTMLImageElement>();
const observer: lozad.Observer | null = window ? lozad(".cia-lazy") : null;

const cardTabIndex = computed(() => {
  return props.clickableCard ? "0" : "-1";
});
const cardStyle = computed(() => {
  return { cursor: props.clickableCard ? "pointer" : "default" };
});
const cardClass = computed(() => {
  let classNames = ``;

  if (props.displayBorder) {
    classNames += `border border-${props.borderVariant}`;
  } else {
    classNames += "border-0";
  }

  if (props.clickableCard) {
    classNames += " card-focus";
  }

  classNames += ` bg-${props.bgVariant}`;
  return classNames;
});
const cardBodyClass = computed(() => {
  return `bg-${props.bgVariant}`;
});
const ctaClass = computed(() => {
  return `fs-${props.ctaTextSize} text-${props.ctaTextVariant} fw-${props.ctaTextWeight}`;
});
const titleClass = computed(() => {
  return `h3-${props.titleSize} text-${props.titleVariant}`;
});

const handleCardClick = (event: Event) => {
  if (props.clickableCard) {
    emits("cardClick", event);
  }
};

const handleCtaClick = (event: Event) => {
  event.stopPropagation();
  emits("ctaClick", event);
};

const handleCardEnter = (event: Event) => {
  if (props.clickableCard) {
    emits("cardEnter", event);
  }
};

watch(
  () => props.imageSource,
  () => {
    if (typeof window !== "undefined" && window !== null && observer !== null) {
      image.value?.removeAttribute("data-loaded");
      observer.triggerLoad(image.value!);
    }
  },
  // triggering post to make sure lozad gets the new attribute value from the dom
  { flush: "post" }
);

onMounted(() => {
  if (typeof window !== "undefined" && window !== null) {
    observer !== null ? observer.observe() : null;
  }
});
</script>

<style lang="scss" scoped>
.cia-image {
  height: 200px;
  object-fit: cover;
  object-position: top;
  transition: transform 0.3s ease;
  border-radius: 0;
}

.cia-details {
  background: var(--rds-white, #ffffff);
  position: relative;
  top: -32px;
  transition: top 0.3s ease;
  min-height: 100px;
}

.cia-card:hover .cia-details {
  top: -45px;
}

.cia-card:hover .cia-image {
  transform: scale(1.1);
}
</style>
